import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import { Router, RouterModule } from '@angular/router';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { ApplicationSelectorComponent } from '../../application-selector/application-selector.component';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { KnowledgeSourceWithSuggestedFlag, isTrainingFinished } from '../../ai-knowledge-util';

import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { WebChatWidgetService } from '../../services/web-chat-widget.service';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';

import { MatButton } from '@angular/material/button';
import { KnowledgeSourceConfig, KnowledgeSourceKey, TrainingProgress } from '@vendasta/embeddings';
import { KnowledgeCreatedEvent, StepChangeEvent } from '../common/interfaces';
import { TrainingProgressComponent } from '../../shared/training-progress/training-progress.component';
import {
  TrainingProgressConfig,
  shouldShowTrainingProgress,
} from '../../shared/training-progress/training-progress.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defer, from, retry, map, take } from 'rxjs';

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  config?: KnowledgeSourceConfig;
  isSuggested?: boolean;
  stepOption?: string;
  excludeAddToAIStep?: boolean;
  currentAppId?: string;
}

@Component({
  selector: 'ai-knowledge-custom-data',
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    RouterModule,
    GalaxySnackbarModule,
    MatStepperModule,
    MatIconModule,
    ApplicationSelectorComponent,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    TrainingProgressComponent,
  ],
  providers: [
    AiKnowledgeService,
    WebChatWidgetService,
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  templateUrl: './custom-data.component.html',
  styleUrls: ['./custom-data.component.scss'],
})
export class CustomDataComponent implements OnInit, KnowledgeCreatedEvent, StepChangeEvent {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('applicationSelector') applicationSelector!: ApplicationSelectorComponent;

  private readonly snackbarService = inject(SnackbarService);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly webChatWidgetService = inject(WebChatWidgetService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly otherAIDisclaimer = signal('');
  readonly isLoading = signal(true);

  // a string corresponding to the current step should be emitted whenever a step changes
  // this will be used to update the address to the current step, so that it can be linked to directly
  stepChange = new EventEmitter<string>();
  excludeAddToAIStep = false;
  // a list of strings corresponding to the step indexes for a stepper
  steps: string[] = [''];
  // the current step
  selectedStep = 0;

  form: FormGroup;
  knowledgeSource: KnowledgeSourceWithSuggestedFlag | null = null;
  knowledgeCreated = new EventEmitter<string>();

  delayProgressScreenClosure = false;
  submitDisabled = signal(false);
  shouldShowDone = signal(false);

  get trainingProgressConfig(): TrainingProgressConfig {
    return {
      trainingInfo: this.knowledgeSource?.training ?? null,
      delayProgressScreenClosure: this.delayProgressScreenClosure,
    };
  }

  shouldShowTrainingProgress(): boolean {
    return shouldShowTrainingProgress(this.trainingProgressConfig, this.shouldShowDone());
  }

  constructor(
    readonly destroyRef: DestroyRef,
    public dialogRef: MatDialogRef<CustomDataComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
  ) {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      content: new FormControl('', [Validators.required]),
    });
  }

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;

    if (this.data) {
      if (this.data?.knowledgeSourceKey) {
        await this.knowledgeService
          .getKnowledgeSource(this.data.knowledgeSourceKey.knowledgeSourceId, this.data.knowledgeSourceKey.namespace)
          .then(
            (knowledgeSource) => {
              this.knowledgeSource = knowledgeSource as KnowledgeSourceWithSuggestedFlag;
            },
            (_) => {
              this.knowledgeSource = null;
            },
          );

        this.otherAIDisclaimer.set(
          await this.knowledgeService.getOtherAIDisclaimerText(
            this.data.knowledgeSourceKey.knowledgeSourceId,
            this.data.currentAppId,
          ),
        );
      }

      this.excludeAddToAIStep = this.data.excludeAddToAIStep ?? false;
    }

    if (!this.excludeAddToAIStep) {
      this.steps.push('appSelection');
    }
    this.form = new FormGroup({
      name: new FormControl(this.knowledgeSource?.name || '', [Validators.required]),
      content: new FormControl(this.knowledgeSource?.config?.customDataConfig?.text || '', [Validators.required]),
    });

    if (this.knowledgeSource && !isTrainingFinished(this.knowledgeSource.training)) {
      this.pollUntilTrainingFinishes(this.knowledgeSource.id);
    }

    this.isLoading.set(false);
  }

  async submit(): Promise<void> {
    if (!this.hasUnsavedChanges()) {
      if (!this.onLastStep()) this.stepper.next();
      else this.close();

      return;
    }

    if (!this.isFormValid()) {
      this.snackbarService.openErrorSnack('COMMON.INVALID_FORM');
      return;
    }

    switch (this.selectedStep) {
      case 0: {
        const name = this.form.get('name')?.value;
        const config = {
          customDataConfig: {
            text: this.form.get('content')?.value,
          },
        };
        const ksId = await this.knowledgeService.createOrUpdateKnowledgeSource(this.knowledgeSource, name, config);
        this.knowledgeCreated.emit(ksId);

        this.form.markAsPristine();
        this.pollUntilTrainingFinishes(ksId);
        return;
      }
      case 1: {
        await this.applicationSelector.setAppsForKnowledgeSource();
        // Close directly without warning check since we just saved
        this.stepChange.emit('');
        this.dialogRef.close();
        return;
      }
    }
  }

  private isFormValid(): boolean {
    switch (this.selectedStep) {
      case 0: {
        const controls = [this.form.get('name'), this.form.get('content')];
        let valid = true;
        controls.forEach((c) => {
          c?.markAsDirty();
          if (!c?.valid) {
            valid = false;
          }
        });
        return valid;
      }
      case 1: {
        return true;
      }
    }
    return false;
  }

  hasUnsavedChanges(): boolean {
    switch (this.selectedStep) {
      case 0: {
        return this.form?.dirty;
      }
      case 1: {
        return this.applicationSelector?.hasUnsavedChanges;
      }
    }
    return false;
  }

  stepChanged(event: StepperSelectionEvent): void {
    this.selectedStep = event?.selectedIndex;
    this.stepChange.emit(this.steps[this.selectedStep]);
  }

  showBack(): boolean {
    return this.selectedStep > 0;
  }

  back(): void {
    this.warnUnsavedChanged(() => {
      this.stepper?.previous();
    });
  }

  onLastStep(): boolean {
    return this.selectedStep === this.steps.length - 1;
  }

  async createWebChatWidget(): Promise<void> {
    await this.webChatWidgetService.createWebChatWidget({
      dialogRef: this.dialogRef,
      knowledgeSource: this.knowledgeSource,
      applicationSelector: this.applicationSelector,
    });
  }

  getTitle(): string {
    if (!this.data?.knowledgeSourceKey) {
      return 'ADD_KNOWLEDGE';
    }
    return 'EDIT_KNOWLEDGE.TITLE';
  }

  warnUnsavedChanged(func: () => void): void {
    if (!this.hasUnsavedChanges()) {
      func();
      return;
    }

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('UNSAVED_CHANGES.TITLE'),
        message: this.translateService.instant('UNSAVED_CHANGES.MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        cancelButtonText: this.translateService.instant('COMMON.NO'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          func();
        }
      });
  }

  close(): void {
    this.warnUnsavedChanged(() => {
      this.stepChange.emit('');
      this.dialogRef.close();
    });
  }

  getSubmitAction(): string {
    if (this.onLastStep()) {
      return 'COMMON.SAVE';
    }
    return 'COMMON.NEXT';
  }

  pollUntilTrainingFinishes(ksId: string): void {
    this.submitDisabled.set(true);
    if (this.onLastStep()) {
      this.shouldShowDone.set(true);
    }
    defer(() => from(this.knowledgeService.getKnowledgeSource(ksId, this.knowledgeService.buildNamespaceInterface())))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        map((ks) => {
          this.knowledgeSource = ks;
          if (!isTrainingFinished(this.knowledgeSource.training)) {
            this.submitDisabled.set(true);
            this.delayProgressScreenClosure = true;
            // throw error to poll again
            throw false;
          }

          setTimeout(() => {
            if (!this.onLastStep()) {
              this.stepper.next();
              // This line fixes the console error message: "ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked."
              this.cdr.detectChanges();
            }
            this.delayProgressScreenClosure = false;
            this.submitDisabled.set(false);
          }, 1000);
        }),
        retry({ delay: 5000 }),
      )
      .subscribe();
  }

  protected readonly TrainingProgress = TrainingProgress;
}
